<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { useDialog } from 'naive-ui';
import GaiaButton from '@/components/design/base/GaiaButton.vue';
import GaiaUploadButton from '@/components/design/base/GaiaUploadButton.vue';
import GaiaModal from '@/components/design/base/GaiaModal.vue';
import GaiaSpinner from '@/components/design/base/GaiaSpinner.vue';
import GaiaText from '@/components/design/base/GaiaText.vue';
import GaiaVaultUploadPanel from '@/components/design/domain/fileVault/GaiaVaultUploadPanel.vue';
import type { VaultUploadPanelApi } from '@/components/design/domain/fileVault/vaultUploadPanel';
import { useVaultStore } from '@/stores/vaultStore';
import { isOfficeAddinHost } from '@/office/utils';
import { logger } from '@/utils/logger';
import { useGaiaToast } from '@/composables/useGaiaToast';
import type { UploadedVaultRevision } from '@/composables/useVaultUpload';

/**
 * The vault's upload dialog: `GaiaVaultUploadPanel` in a modal, plus the Word
 * add-in's "upload the open document". The vault and data room state their
 * folder; `selectableDestination` (the app-wide dialog) lets the user pick one.
 */
const props = withDefaults(
  defineProps<{
    show: boolean;
    parentDirectoryUid?: string | null;
    parentDirectoryName?: string;
    isFolderUpload?: boolean;
    autoLoadCurrentDoc?: boolean;
    /** Let the user pick the target folder — for uploads that have none of their own. */
    selectableDestination?: boolean;
    /** The folder a selectable destination can't climb above (e.g. a data room). */
    scopeRootUid?: string;
    /** That folder's name, the picker's first crumb. */
    scopeRootLabel?: string;
    /** Files already chosen elsewhere (a drop, a quick action), listed on open. */
    initialFiles?: File[];
    accept?: string;
    max?: number;
    /** Unpack an uploaded archive in place — off for flows that hand the file on as-is. */
    unpackArchives?: boolean;
  }>(),
  {
    parentDirectoryUid: null,
    parentDirectoryName: undefined,
    scopeRootUid: undefined,
    scopeRootLabel: undefined,
    initialFiles: () => [],
    accept: undefined,
    max: undefined,
    unpackArchives: true,
  }
);

const emit = defineEmits<{
  'update:show': [value: boolean];
  /** `complete` is false when some files failed and the dialog stays open to list them. */
  uploaded: [complete: boolean];
  /** Every file that landed, in list order — only after all of them did. */
  'uploaded-files': [files: UploadedVaultRevision[]];
}>();

const vaultStore = useVaultStore();
const message = useGaiaToast();
const dialog = useDialog();

const panelRef = ref<VaultUploadPanelApi | null>(null);
const panelReady = ref(false);
const uploading = ref(false);
const isLoadingCurrentDocument = ref(false);
const currentOfficeDocumentFile = ref<File | null>(null);

type OfficeAsyncResult<T> = {
  error: { message: string };
  status: unknown;
  value: T;
};

type OfficeFileProperties = {
  url: string;
};

type OfficeSlice = {
  data: ArrayLike<number>;
};

type OfficeFileHandle = {
  closeAsync: (callback?: () => void) => void;
  getSliceAsync: (
    index: number,
    callback: (result: OfficeAsyncResult<OfficeSlice>) => void
  ) => void;
  sliceCount: number;
};

// A selectable destination always uploads files handed to it, never the open document.
const canUseCurrentOfficeDocument = computed<boolean>(
  () => isOfficeAddinHost() && !props.isFolderUpload && !props.selectableDestination
);
/** The open Word document stands in for picked files once it has loaded. */
const panelFiles = computed<File[]>(() =>
  currentOfficeDocumentFile.value ? [currentOfficeDocumentFile.value] : props.initialFiles
);
const modalTitle = computed<string>(() =>
  props.isFolderUpload ? 'Upload Folder' : 'Upload Files'
);

watch(
  () => props.show,
  show => {
    if (!show) {
      currentOfficeDocumentFile.value = null;
      isLoadingCurrentDocument.value = false;
      return;
    }
    if (props.autoLoadCurrentDoc || canUseCurrentOfficeDocument.value) {
      void useCurrentOfficeDocument();
    }
  },
  { immediate: true }
);

function closeModal(): void {
  if (uploading.value) {
    dialog.warning({
      title: 'Close',
      content: 'Uploads in progress will be canceled. Are you sure you want to close?',
      positiveText: 'Close',
      negativeText: 'Cancel',
      onPositiveClick: () => {
        emit('update:show', false);
        vaultStore.setSelectedItems([]);
      },
    });
    return;
  }
  emit('update:show', false);
  vaultStore.setSelectedItems([]);
}

async function runUpload(): Promise<void> {
  const panel = panelRef.value;
  if (!panel) return;
  uploading.value = true;
  try {
    const uploaded = await panel.upload();
    if (!uploaded) {
      // A folder upload lands partly; refresh the listing for what did.
      if (props.isFolderUpload) emit('uploaded', false);
      return;
    }
    emit('uploaded-files', uploaded);
    emit('uploaded', true);
    emit('update:show', false);
  } finally {
    uploading.value = false;
  }
}

function getOfficeResult<T>(
  executor: (callback: (result: OfficeAsyncResult<T>) => void) => void
): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    executor(result => {
      if (result.status === window.Office?.AsyncResultStatus?.Succeeded) {
        resolve(result.value);
        return;
      }

      reject(new Error(result.error.message || 'Office action failed.'));
    });
  });
}

function guessCurrentDocumentName(url: string): string {
  if (!url) {
    return 'Current Document.docx';
  }

  try {
    const normalizedUrl = url.replace(/\\/g, '/');
    const lastSegment = decodeURIComponent(normalizedUrl.split('/').pop() ?? '').trim();
    if (!lastSegment) {
      return 'Current Document.docx';
    }
    return /\.docx$/i.test(lastSegment) ? lastSegment : `${lastSegment}.docx`;
  } catch {
    return 'Current Document.docx';
  }
}

async function getCurrentOfficeDocumentFile(): Promise<File> {
  const document = window.Office?.context?.document;
  if (!document) {
    throw new Error('Office document context is not available.');
  }

  const fileProperties = await getOfficeResult<OfficeFileProperties>(callback => {
    document.getFilePropertiesAsync(callback);
  });
  const fileName = guessCurrentDocumentName(fileProperties.url || '');

  const officeFile = await getOfficeResult<OfficeFileHandle>(callback => {
    document.getFileAsync('compressed', { sliceSize: 1024 * 1024 }, callback);
  });

  try {
    const slices: Uint8Array<ArrayBuffer>[] = [];

    for (let index = 0; index < officeFile.sliceCount; index++) {
      const slice = await getOfficeResult<OfficeSlice>(callback => {
        officeFile.getSliceAsync(index, callback);
      });
      slices.push(Uint8Array.from(Array.from(slice.data)));
    }

    return new File(slices, fileName, {
      type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      lastModified: Date.now(),
    });
  } finally {
    await new Promise<void>(resolve => {
      officeFile.closeAsync(() => {
        resolve();
      });
    });
  }
}

async function useCurrentOfficeDocument(): Promise<void> {
  if (!canUseCurrentOfficeDocument.value || isLoadingCurrentDocument.value) {
    return;
  }

  isLoadingCurrentDocument.value = true;

  try {
    const file = await getCurrentOfficeDocumentFile();
    currentOfficeDocumentFile.value = file;
    message.success(`Selected current document: ${file.name}`);
  } catch (error) {
    logger.error('Failed to load current Office document:', error);
    message.error(
      error instanceof Error ? error.message : 'Failed to load the current Word document.'
    );
  } finally {
    isLoadingCurrentDocument.value = false;
  }
}
</script>

<template>
  <gaia-modal
    :show="props.show"
    width="600px"
    :mask-closable="false"
    @update:show="(value: boolean) => emit('update:show', value)"
  >
    <template #header>
      <gaia-text size="subtitle" weight="semibold" tone="ink">{{ modalTitle }}</gaia-text>
    </template>

    <n-flex vertical :size="14" :wrap="false">
      <n-flex
        v-if="canUseCurrentOfficeDocument && isLoadingCurrentDocument && !currentOfficeDocumentFile"
        align="center"
        :size="9"
        :wrap="false"
      >
        <gaia-spinner size="small" />
        <gaia-text size="control" tone="tertiary">Loading the open Word document…</gaia-text>
      </n-flex>

      <gaia-vault-upload-panel
        ref="panelRef"
        :active="props.show"
        :directory-uid="props.parentDirectoryUid"
        :directory-label="props.parentDirectoryName || 'Home'"
        :selectable-destination="props.selectableDestination"
        :scope-root-uid="props.scopeRootUid"
        :scope-root-label="props.scopeRootLabel"
        :initial-files="panelFiles"
        :accept="props.accept"
        :max="props.max"
        :unpack-archives="props.unpackArchives"
        :folder-upload="props.isFolderUpload"
        :renamable="canUseCurrentOfficeDocument"
        :hide-dropzone="canUseCurrentOfficeDocument"
        @update:ready="panelReady = $event"
      />
    </n-flex>

    <template #footer>
      <n-flex justify="end" :size="8">
        <gaia-button size="small" :disabled="uploading" @click="closeModal">Cancel</gaia-button>
        <gaia-upload-button
          size="sm"
          :icon="false"
          :loading="uploading"
          :disabled="!panelReady || uploading"
          @click="runUpload"
        />
      </n-flex>
    </template>
  </gaia-modal>
</template>
